import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

function LoadMoreRecommendations() {
    const { token } = useParams();
    const [status, setStatus] = useState('loading'); // loading | success | completed | invalid | error
    const [message, setMessage] = useState('');
    const hasTriggered = useRef(false);
    useEffect(() => {
        if (!token || hasTriggered.current) {
            setStatus('invalid');
            return;
        }
        hasTriggered.current = true;
        const trigger = async () => {
            try {
                const response = await axios.post(`${API_BASE_URL}/recommendations/load-more`, { uuid: token });
                if (response.data.success) {
                    if (response.data.completed) {
                        setStatus('completed');
                    } else {
                        setStatus('success');
                    }
                    setMessage(response.data.message || 'Done!');
                } else {
                    setStatus('error');
                    setMessage(response.data.message || 'Something went wrong.');
                }
            } catch (err) {
                const errMsg = err.response?.data?.message;
                if (err.response?.data?.completed) {
                    setStatus('completed');
                    setMessage(errMsg || 'You have already received all available recommendations.');
                } else if (err.response?.status === 404) {
                    setStatus('invalid');
                    setMessage('Invalid request.');
                } else {
                    setStatus('error');
                    setMessage(errMsg || 'Something went wrong. Please try again.');
                }
            }
        };

        trigger();
    }, [token]);

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <div className="lmr-state">
                        <div className="lmr-spinner">
                            <div className="lmr-spinner-ring"></div>
                        </div>
                        <h2 className="lmr-title">Finding Vendors...</h2>
                        <p className="lmr-sub">We are fetching the best vendor recommendations for you. Please wait.</p>
                    </div>
                );

            case 'success':
                return (
                    <div className="lmr-state">
                        <div className="lmr-icon lmr-icon-success">
                            <i className="bi bi-whatsapp"></i>
                        </div>
                        <h2 className="lmr-title">Sent to Your WhatsApp!</h2>
                        <p className="lmr-sub">More vendor recommendations have been sent to your WhatsApp successfully. Check your messages!</p>
                        <Link to="/" className="lmr-btn lmr-btn-primary">Back to Home</Link>
                    </div>
                );

            case 'completed':
                return (
                    <div className="lmr-state">
                        <div className="lmr-icon lmr-icon-info">
                            <i className="bi bi-check-circle-fill"></i>
                        </div>
                        <h2 className="lmr-title">All Done!</h2>
                        <p className="lmr-sub">You have already received all available recommendations. We hope you found the right vendor!</p>
                        <Link to="/" className="lmr-btn lmr-btn-outline">Back to Home</Link>
                    </div>
                );

            case 'invalid':
                return (
                    <div className="lmr-state">
                        <div className="lmr-icon lmr-icon-warning">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                        </div>
                        <h2 className="lmr-title">Invalid Request</h2>
                        <p className="lmr-sub">This link is invalid or has expired. Please start a new enquiry from our website.</p>
                        <Link to="/" className="lmr-btn lmr-btn-outline">Go to Home</Link>
                    </div>
                );

            case 'error':
            default:
                return (
                    <div className="lmr-state">
                        <div className="lmr-icon lmr-icon-error">
                            <i className="bi bi-x-circle-fill"></i>
                        </div>
                        <h2 className="lmr-title">Something Went Wrong</h2>
                        <p className="lmr-sub">{message || 'An unexpected error occurred. Please try again later.'}</p>
                        <Link to="/" className="lmr-btn lmr-btn-outline">Go to Home</Link>
                    </div>
                );
        }
    };

    return (
        <div className="lmr-wrapper">
            <div className="lmr-bg-gradient"></div>
            <div className="lmr-card">
                <div className="lmr-logo">
                    <img src="/logo-mangoloops.png" alt="Mangoloops" />
                </div>
                {renderContent()}
            </div>

            <style>{`
                .lmr-wrapper {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px 16px;
                    background: linear-gradient(135deg, #f0f4ff 0%, #fafbff 50%, #f0fff4 100%);
                    position: relative;
                    overflow: hidden;
                    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
                }
                .lmr-bg-gradient {
                    position: absolute;
                    inset: 0;
                    background: radial-gradient(ellipse at 20% 50%, rgba(13,110,253,0.08) 0%, transparent 60%),
                                radial-gradient(ellipse at 80% 20%, rgba(37,211,102,0.08) 0%, transparent 60%);
                    pointer-events: none;
                }
                .lmr-card {
                    position: relative;
                    background: #ffffff;
                    border-radius: 24px;
                    padding: 48px 40px;
                    max-width: 480px;
                    width: 100%;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06);
                    text-align: center;
                    animation: lmrSlideUp 0.4s ease;
                }
                @keyframes lmrSlideUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .lmr-logo {
                    margin-bottom: 32px;
                }
                .lmr-logo img {
                    height: 36px;
                    object-fit: contain;
                }
                .lmr-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                }
                .lmr-icon {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 36px;
                    margin-bottom: 8px;
                    animation: lmrPop 0.4s 0.1s both;
                }
                @keyframes lmrPop {
                    from { transform: scale(0.6); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .lmr-icon-success { background: #e6f9ee; color: #25d366; }
                .lmr-icon-info    { background: #e8f0fe; color: #0d6efd; }
                .lmr-icon-warning { background: #fff8e1; color: #f59e0b; }
                .lmr-icon-error   { background: #ffeaea; color: #dc3545; }
                .lmr-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0;
                }
                .lmr-sub {
                    font-size: 15px;
                    color: #64748b;
                    line-height: 1.6;
                    margin: 0;
                    max-width: 340px;
                }
                .lmr-btn {
                    display: inline-block;
                    padding: 12px 32px;
                    border-radius: 50px;
                    font-size: 15px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: transform 0.15s, box-shadow 0.15s;
                    margin-top: 8px;
                }
                .lmr-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.12); text-decoration: none; }
                .lmr-btn-primary { background: linear-gradient(135deg, #25d366 0%, #128c7e 100%); color: #fff; }
                .lmr-btn-outline { background: transparent; color: #0d6efd; border: 2px solid #0d6efd; }
                .lmr-btn-outline:hover { background: #0d6efd; color: #fff; }

                /* Spinner */
                .lmr-spinner {
                    width: 72px;
                    height: 72px;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .lmr-spinner-ring {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    border: 5px solid #e8f0fe;
                    border-top-color: #0d6efd;
                    animation: lmrSpin 0.9s linear infinite;
                }
                @keyframes lmrSpin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 480px) {
                    .lmr-card { padding: 36px 24px; }
                    .lmr-title { font-size: 20px; }
                }
            `}</style>
        </div>
    );
}

export default LoadMoreRecommendations;
